/**
 * Team → Questions.
 *
 * The team's full question log: what each employee asked, who it was
 * addressed to, and how it ended. Pending rows are answerable (or deniable)
 * here — the same writes the per-user confirmation queue performs, so either
 * resolution resumes the parked job.
 *
 * Rows arrive in the server's triage order: pending first with blocking
 * pinned and the oldest on top, then resolved history newest-first. An admin
 * sees every question, including the ones a chief of staff answered without
 * the owner ever being asked. That audit path is the reason the log is a
 * page of its own rather than a filter on the employee drawer.
 *
 * `?id=<uuid>` shows that one question – the target of an ID link
 * ("question d2de109f", `lib/idLinks.ts`).
 */
import { useCallback, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { adminApi } from "../../api/admin";
import { LiveIndicator } from "../../components/LiveIndicator";
import { PageHeader } from "../../components/PageHeader";
import { QueryBoundary } from "../../components/QueryBoundary";
import { ShortId } from "../../components/ShortId";
import { useToast } from "../../components/toast/context";
import { QUESTION_FILTERS, type QuestionFilter, agentLabel } from "../../lib/aiTeam";
import { useLiveUpdates } from "../../lib/hooks/useLiveUpdates";
import { useUrlFilter } from "../../lib/hooks/useUrlFilter";
import { QuestionCard, QuestionStatusChips } from "./QuestionCard";

export function QuestionsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useUrlFilter<QuestionFilter>(
    "status",
    QUESTION_FILTERS.map((f) => f.value),
    "pending",
  );
  // The asker filter is free-form (an agent uuid), so it reads the URL
  // directly rather than through the allow-listed useUrlFilter.
  const [params, setParams] = useSearchParams();
  const requestedBy = params.get("asker") ?? "";
  const setRequestedBy = (next: string) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next) out.set("asker", next);
        else out.delete("asker");
        return out;
      },
      { replace: true },
    );
  const agentsQuery = adminApi.aiTeam.agents();
  const focusId = params.get("id");
  const logQuery = adminApi.aiTeam.questions(filter, {
    requestedBy: requestedBy || undefined,
  });
  const focusQuery = adminApi.aiTeam.question(focusId);
  const questionsQuery = focusId ? focusQuery : logQuery;
  const answerMutation = adminApi.aiTeam.answerQuestion();
  const denyMutation = adminApi.aiTeam.denyQuestion();
  const toast = useToast();

  // A question row landing or resolving repaints the log live — same
  // debounced whole-subtree invalidate as the AI Team page.
  const topics = useMemo(
    () => (agentsQuery.data ?? []).map((a) => `questions:agent:${a.id}`),
    [agentsQuery.data],
  );
  const debounceRef = useRef<number | null>(null);
  const onLiveEvent = useCallback(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      qc.invalidateQueries({ queryKey: ["admin", "ai-team"] });
    }, 500);
  }, [qc]);
  const { status: liveStatus } = useLiveUpdates(topics, onLiveEvent);

  return (
    <>
      <PageHeader
        title="AI Questions"
        description="Everything the AI employees asked — open ones answerable here, resolved ones kept for audit."
        actions={<LiveIndicator status={liveStatus} />}
      />

      {focusId ? (
        <div className="flex items-center gap-2 mb-5 text-sm text-ink-low">
          Question <ShortId id={focusId} /> ·{" "}
          <Link className="link" to="/team/questions">
            All questions
          </Link>
        </div>
      ) : (
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <QuestionStatusChips value={filter} onChange={setFilter} />
        <select
          className="input"
          style={{ minWidth: 160 }}
          aria-label="Filter by employee"
          value={requestedBy}
          onChange={(e) => setRequestedBy(e.target.value)}
        >
          <option value="">all employees</option>
          {(agentsQuery.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {agentLabel(a)}
            </option>
          ))}
        </select>
        <div className="text-sm text-ink-low ml-2">
          <span className="tnum font-medium text-ink">
            {questionsQuery.data?.length ?? 0}
          </span>{" "}
          entries · blocking pinned first
        </div>
      </div>
      )}

      <QueryBoundary
        query={questionsQuery}
        empty={
          filter === "pending" ? (
            <div className="empty-state">
              <div className="title">Nothing needs an answer</div>
              <p className="hint">
                No open questions{requestedBy ? " from this employee" : ""} —
                resolved history is one filter away.
              </p>
            </div>
          ) : (
            <div className="empty-state">
              <div className="title">Nothing here</div>
              <p className="hint">
                No matching questions in the retained log for this filter.
              </p>
            </div>
          )
        }
        isEmpty={(rows) => rows.length === 0}
      >
        {(rows) => (
          <div className="space-y-3">
            {rows.map((q) => (
              <QuestionCard
                key={q.id}
                question={q}
                showAsker
                onAnswer={async (body) => {
                  try {
                    await answerMutation.mutateAsync({ id: q.id, ...body });
                    toast.ok("Answered");
                  } catch (e) {
                    toast.error(e);
                    throw e; // Keeps the draft — the card clears on success only.
                  }
                }}
                onDeny={() =>
                  denyMutation.mutate(
                    { id: q.id },
                    {
                      onSuccess: (r) =>
                        r.status === "denied"
                          ? toast.ok("Denied")
                          : toast.error(`Already ${r.status} — deny arrived late`),
                      onError: (e) => toast.error(e),
                    },
                  )
                }
              />
            ))}
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
